import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  Info,
  RefreshCw,
  Syringe,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { NudgeBanner } from "../../components/ui/NudgeBanner";
import { PageBanner } from "../../components/ui/PageBanner";
import {
  AggregatedHealthAdvice,
  getHealthAdvice,
  VaccinationRequirement,
} from "../../services/api/travelHealth";
import { getTravel, Travel } from "../../services/api/travels";
import { useAuthStore } from "../../stores/authStore";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import { toDateOnly } from "../../utils/travelHealthLabels";

const sevColor = {
  critical: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    badge: "bg-red-100",
  },
  high: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-700",
    badge: "bg-orange-100",
  },
  medium: {
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-700",
    badge: "bg-yellow-100",
  },
  low: {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-600",
    badge: "bg-green-100",
  },
};

export const VaccinationsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const { user } = useAuthStore();
  const [travel, setTravel] = useState<Travel | null>(null);
  const [advice, setAdvice] = useState<AggregatedHealthAdvice | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) loadTravel(id);
  }, [id]);

  const loadTravel = async (travelId: string) => {
    try {
      setLoading(true);
      const data = await getTravel(travelId);
      setTravel(data);
      await loadAdvice(data);
    } catch (err: any) {
      console.error("Failed to load travel:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAdvice = useCallback(
    async (travelData: Travel) => {
      if (!travelData.country || !travelData.departureDate) return;
      try {
        setLoadingAdvice(true);
        setError(null);
        const data = await getHealthAdvice(
          travelData.country,
          toDateOnly(travelData.departureDate),
          toDateOnly(travelData.returnDate ?? travelData.departureDate),
          travelData.tripType ?? "flight",
          user?.country ?? undefined,
        );
        setAdvice(data);
      } catch (err: any) {
        setError(
          t.healthDataLoadError ?? "Could not load health recommendations.",
        );
      } finally {
        setLoadingAdvice(false);
      }
    },
    [user?.country, t],
  );

  const VaccineCard = ({
    vac,
    tag,
  }: {
    vac: VaccinationRequirement;
    tag: "required" | "recommended";
  }) => {
    const colors = tag === "required" ? sevColor.critical : sevColor.medium;
    const Icon = tag === "required" ? AlertCircle : Clock;
    return (
      <div className={`p-4 rounded-lg border ${colors.bg} ${colors.border}`}>
        <div className="flex items-start gap-3">
          <Icon className={`h-5 w-5 ${colors.text} mt-0.5 shrink-0`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-semibold text-gray-900">{vac.vaccineName}</h3>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.text} ${colors.badge}`}
              >
                {tag === "required"
                  ? t.vaccinationRequired
                  : t.vaccinationRecommended}
              </span>
              {vac.boosterRequired && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700">
                  {t.boosterRequired ?? "Booster"}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-600 space-y-0.5">
              {vac.minDaysBeforeDeparture && (
                <p>
                  {t.atLeast} <strong>{vac.minDaysBeforeDeparture}</strong>{" "}
                  {t.daysBeforeDeparture}
                </p>
              )}
              {vac.validityDays && (
                <p>
                  {t.validFor}{" "}
                  <strong>{Math.round(vac.validityDays / 365)}</strong>{" "}
                  {t.years ?? "years"}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                {t.source ?? "Source"}: {vac.source?.name}
              </p>
            </div>
          </div>
          <button className="shrink-0 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-xs font-medium flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {t.bookAppointment ?? "Book"}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const required = advice?.requiredVaccinations ?? [];
  const recommended = advice?.recommendedVaccinations ?? [];

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/traveler/travels")}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <PageBanner
          icon={Syringe}
          title={t.vaccinationDetails ?? "Vaccination Details"}
          description={travel ? `${travel.destination}, ${travel.country}` : ""}
        />
      </div>

      {advice && (
        <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-xl px-4 py-3 text-sm text-primary-800">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 shrink-0" />
            <span>
              {t.dataFrom ?? "Data from"}:{" "}
              {advice.sources?.map((s) => s.name).join(", ")} ·{" "}
              {t.lastUpdated ?? "Updated"}: {fmt.date(advice.lastUpdated)}
            </span>
          </div>
          <button
            onClick={() => travel && loadAdvice(travel)}
            disabled={loadingAdvice}
            className="flex items-center gap-1 hover:text-primary-600"
          >
            <RefreshCw
              className={`h-4 w-4 ${loadingAdvice ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      )}

      {error && (
        <NudgeBanner
          variant="warning"
          icon={<AlertCircle className="h-4 w-4" />}
          className="text-sm"
        >
          {error}
        </NudgeBanner>
      )}
      {loadingAdvice && (
        <div className="flex items-center gap-3 py-2 text-sm text-gray-500">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
          {t.loadingHealthData}
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          {t.requiredVaccinations ?? "Required Vaccinations"}
          <span className="ml-auto text-sm font-normal text-gray-500">
            {required.length}
          </span>
        </h2>
        {required.length === 0 ? (
          <p className="text-sm text-gray-500">{t.noRequiredVaccinations}</p>
        ) : (
          <div className="space-y-3">
            {required.map((v, i) => (
              <VaccineCard key={i} vac={v} tag="required" />
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-yellow-600" />
          {t.recommendedVaccinations ?? "Recommended Vaccinations"}
          <span className="ml-auto text-sm font-normal text-gray-500">
            {recommended.length}
          </span>
        </h2>
        {recommended.length === 0 ? (
          <p className="text-sm text-gray-500">{t.noRecommendedVaccinations}</p>
        ) : (
          <div className="space-y-3">
            {recommended.map((v, i) => (
              <VaccineCard key={i} vac={v} tag="recommended" />
            ))}
          </div>
        )}
      </div>

      {[...required, ...recommended].filter((v) => v.minDaysBeforeDeparture)
        .length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t.vaccinationTimeline}
          </h2>
          <div className="space-y-3">
            {[...required, ...recommended]
              .filter((v) => v.minDaysBeforeDeparture)
              .sort(
                (a, b) =>
                  (b.minDaysBeforeDeparture ?? 0) -
                  (a.minDaysBeforeDeparture ?? 0),
              )
              .map((v, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-36 text-sm font-medium text-gray-600 shrink-0">
                    {v.minDaysBeforeDeparture}d {t.before}
                  </div>
                  <div
                    className={`flex-1 rounded-lg p-3 text-sm font-medium ${v.isRequired ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}
                  >
                    {v.vaccineName}
                    {v.isRequired ? ` (${t.required ?? "Required"})` : ""}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
